import { clsx } from 'clsx'
import * as React from 'react'

import { Button } from '../Button'
import { IconButton } from '../IconButton'
import {
  CrosshairIcon,
  FlipHorizontal2Icon,
  FlipVertical2Icon,
  RotateCwIcon,
} from '../icons'
import { Notice } from '../Notice'
import { Slider } from '../Slider'
import { Tabs } from '../Tabs'
import { Text } from '../Text'
import { Tooltip } from '../Tooltip'
import { ToggleGroup } from '../ToggleGroup'
import { UnstyledButton } from '../UnstyledButton'
import { VisuallyHidden } from '../VisuallyHidden'
import { useCallbackRef } from '../../hooks/useCallbackRef'
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect'
import { useResizeObserver } from '../../hooks/useResizeObserver'
import {
  centerNormalizedCrop,
  containImageDimensions,
  createIdentityImageEdit,
  fitCropToAspectRatio,
  flipImageEditHorizontally,
  flipImageEditVertically,
  getCroppedImageDimensions,
  getOrientedImageDimensions,
  getPixelCropGeometry,
  isIdentityImageEdit,
  moveNormalizedCrop,
  resizeNormalizedCrop,
  rotateImageEditCounterclockwise,
  rotateImageEditClockwise,
  snapNormalizedCropToPixels,
} from './imageEditorGeometry'
import type {
  CropHandle,
  ImageDimensions,
  ImageEdit,
  NormalizedCrop,
} from './imageEditorGeometry'
import { DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_DIMENSION } from './renderImageEdit'
import {
  getImageEditCssTransform,
  IMAGE_EDITOR_MAX_STRAIGHTENING,
  IMAGE_EDITOR_MIN_STRAIGHTENING,
} from './imageEditorTransform'
import {
  getImageAppearanceCssFilter,
  IMAGE_EDITOR_MAX_ADJUSTMENT,
  IMAGE_EDITOR_MAX_FILTER_INTENSITY,
  IMAGE_EDITOR_MIN_ADJUSTMENT,
  IMAGE_EDITOR_MIN_FILTER_INTENSITY,
} from './imageEditorAppearance'
import type {
  ImageAppearance,
  ImageFilterPreset,
} from './imageEditorAppearance'
import { ImageAppearanceFilter } from './ImageAppearanceFilter'

import styles from './ImageEditorSurface.module.css'

type ImageEditorSource = Readonly<{
  src: string
  crossOrigin?: 'anonymous' | 'use-credentials' | null
  alt?: string
  width?: number | null
  height?: number | null
}>

type ImageEditorAspectRatio = Readonly<{
  label: string
  value: number
}>

type ImageEditorAspectRatioRange = Readonly<{
  min: number
  max: number
}>

type ImageEditorSurfaceAlternateInteraction = Readonly<{
  /** Accessible name for the image interaction area. */
  canvasLabel: string
  /** Content rendered inside the current crop bounds instead of crop controls. */
  cropOverlay: React.ReactNode
  /** Content rendered relative to the full image stage. */
  stageOverlay?: React.ReactNode
}>

type ImageEditorSurfaceProps = {
  /** The image to edit. Dimensions are read from the image when omitted. */
  source: ImageEditorSource
  /** Optional crop ratios supplied by the consuming product. */
  aspectRatios?: readonly ImageEditorAspectRatio[]
  /** Whether to offer an explicit free-form crop preset. */
  showFreeAspectRatio?: boolean
  /** Optional product limits applied while free-form cropping. */
  freeAspectRatioRange?: ImageEditorAspectRatioRange
  /** Called when the source cannot be decoded by the browser. */
  onSourceLoadError?: () => void
  /** Current transformation state. */
  value: ImageEdit
  /** Called when the transformation state changes. */
  onValueChange: (value: ImageEdit) => void
  /** Crop ratio currently enforced while resizing, or null for free-form crop. */
  selectedAspectRatio: number | null
  /** Called when the enforced crop ratio changes. */
  onSelectedAspectRatioChange: (value: number | null) => void
  /** Called when the visible editor draft becomes valid or invalid. */
  onValidityChange?: (isValid: boolean, issue?: string) => void
  /** Whether validation notices should render inside the adjustments panel. */
  showInlineNotices?: boolean
  /** Product-specific content shown first in the adjustments panel. */
  adjustmentsHeader?: React.ReactNode
  /** Product-specific controls shown with the primary crop controls. */
  cropControls?: React.ReactNode
  /** Product-specific controls shown after the primary image adjustments. */
  additionalControls?: React.ReactNode
  /** Hides product-specific controls without unmounting their state. */
  additionalControlsHidden?: boolean
  /** Product-specific content positioned across the full image bounds. */
  imageOverlay?: React.ReactNode
  /** Product-specific notice positioned over the image stage. */
  stageNotice?: React.ReactNode
  /** Product-specific actions positioned beside the image stage. */
  stageActions?: React.ReactNode
  /** Product-specific content positioned directly beneath the image stage. */
  stageFooter?: React.ReactNode
  /** Replaces crop manipulation while keeping the adjustment controls mounted. */
  alternateInteraction?: ImageEditorSurfaceAlternateInteraction
  /** Whether to expose editing controls or a static metadata preview. */
  mode?: 'edit' | 'preview'
  /** Whether editing controls are disabled. */
  disabled?: boolean
  className?: string
}

type CropAction =
  | Readonly<{ kind: 'move' }>
  | Readonly<{ kind: 'resize'; handle: CropHandle }>

type ActiveCropDrag = Readonly<{
  action: CropAction
  pointerId: number
  originClientX: number
  originClientY: number
  initialCrop: NormalizedCrop
}>

type CropUpdateOptions = Readonly<{
  initialCrop?: NormalizedCrop
  preserveInitialAspectRatio?: boolean
}>

type CropSize = Readonly<Pick<NormalizedCrop, 'width' | 'height'>>

const FALLBACK_DIMENSIONS: ImageDimensions = { width: 1, height: 1 }
const MINIMUM_CROP_DISPLAY_SIZE = 88
const CROP_COMPARISON_TOLERANCE = 0.000_001
const CENTER_CROP_DISABLED_REASON = 'Crop is already centered.'
const CENTER_CROP_TOOLTIP = 'Center the crop within the image.'
const OUTPUT_LIMIT_ERROR =
  'Crop a smaller area to keep the edited image within the supported size.'

const formatStraightening = (straightening: number): string =>
  `${straightening > 0 ? '+' : ''}${straightening}°`

const getStraighteningValueText = (straightening: number): string => {
  if (straightening === 0) return 'No straightening'
  return `${Math.abs(straightening)}° ${straightening > 0 ? 'clockwise' : 'counterclockwise'}`
}

const formatAdjustment = (value: number): string =>
  `${value > 0 ? '+' : ''}${value}%`

type ImageEditorEditView = 'crop-transform' | 'appearance'

const isImageEditorEditView = (value: string): value is ImageEditorEditView =>
  value === 'crop-transform' || value === 'appearance'

type AppearanceAdjustmentKey =
  | 'brightness'
  | 'contrast'
  | 'saturation'
  | 'warmth'

const APPEARANCE_ADJUSTMENTS: readonly Readonly<{
  key: AppearanceAdjustmentKey
  label: string
  getValueText: (value: number) => string
}>[] = [
  {
    key: 'brightness',
    label: 'Brightness',
    getValueText: (value) =>
      value === 0
        ? 'No brightness adjustment'
        : `${Math.abs(value)}% ${value > 0 ? 'brighter' : 'darker'}`,
  },
  {
    key: 'contrast',
    label: 'Contrast',
    getValueText: (value) =>
      value === 0
        ? 'No contrast adjustment'
        : `${Math.abs(value)}% ${value > 0 ? 'more' : 'less'} contrast`,
  },
  {
    key: 'saturation',
    label: 'Saturation',
    getValueText: (value) =>
      value === 0
        ? 'No saturation adjustment'
        : `${Math.abs(value)}% ${value > 0 ? 'more' : 'less'} saturated`,
  },
  {
    key: 'warmth',
    label: 'Warmth',
    getValueText: (value) =>
      value === 0
        ? 'No warmth adjustment'
        : `${Math.abs(value)}% ${value > 0 ? 'warmer' : 'cooler'}`,
  },
]

const FILTER_PRESETS: readonly Readonly<{
  label: string
  value: ImageFilterPreset
}>[] = [
  { label: 'Original', value: 'none' },
  { label: 'Vivid', value: 'vivid' },
  { label: 'Mono', value: 'grayscale' },
  { label: 'Sepia', value: 'sepia' },
]

const getFilterThumbnailAppearance = (
  preset: ImageFilterPreset,
): ImageAppearance => ({
  brightness: 0,
  contrast: 0,
  saturation: 0,
  warmth: 0,
  filterPreset: preset,
  filterIntensity: IMAGE_EDITOR_MAX_FILTER_INTENSITY,
})
const getMinimumDisplayedCropSize = (
  stageDimensions: CropSize | null | undefined,
): CropSize | undefined => {
  if (
    !stageDimensions ||
    stageDimensions.width <= 0 ||
    stageDimensions.height <= 0
  ) {
    return undefined
  }

  return {
    width: Math.min(1, MINIMUM_CROP_DISPLAY_SIZE / stageDimensions.width),
    height: Math.min(1, MINIMUM_CROP_DISPLAY_SIZE / stageDimensions.height),
  }
}

const cropHandles: ReadonlyArray<
  Readonly<{ handle: CropHandle; label: string; className: string }>
> = [
  {
    handle: 'top-left',
    label: 'top left',
    className: styles.topLeft,
  },
  { handle: 'top', label: 'top', className: styles.top },
  {
    handle: 'top-right',
    label: 'top right',
    className: styles.topRight,
  },
  { handle: 'left', label: 'left', className: styles.left },
  { handle: 'right', label: 'right', className: styles.right },
  {
    handle: 'bottom-left',
    label: 'bottom left',
    className: styles.bottomLeft,
  },
  { handle: 'bottom', label: 'bottom', className: styles.bottom },
  {
    handle: 'bottom-right',
    label: 'bottom right',
    className: styles.bottomRight,
  },
]

const cornerCropHandles: ReadonlySet<CropHandle> = new Set([
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
])

const normalizedCropsAreEqual = (
  first: NormalizedCrop,
  second: NormalizedCrop,
): boolean =>
  Math.abs(first.x - second.x) <= CROP_COMPARISON_TOLERANCE &&
  Math.abs(first.y - second.y) <= CROP_COMPARISON_TOLERANCE &&
  Math.abs(first.width - second.width) <= CROP_COMPARISON_TOLERANCE &&
  Math.abs(first.height - second.height) <= CROP_COMPARISON_TOLERANCE

const getProvidedDimensions = (
  width: number | null | undefined,
  height: number | null | undefined,
): ImageDimensions | null => {
  if (
    typeof width !== 'number' ||
    !Number.isFinite(width) ||
    width <= 0 ||
    typeof height !== 'number' ||
    !Number.isFinite(height) ||
    height <= 0
  ) {
    return null
  }

  return {
    width: Math.round(width),
    height: Math.round(height),
  }
}

const replaceCrop = (edit: ImageEdit, crop: NormalizedCrop): ImageEdit => ({
  ...edit,
  crop,
})

const snapEditCropToPixels = (
  edit: ImageEdit,
  orientedDimensions: ImageDimensions,
): ImageEdit =>
  replaceCrop(edit, snapNormalizedCropToPixels(edit.crop, orientedDimensions))

const handleSupportsHorizontalResize = (handle: CropHandle): boolean =>
  handle === 'left' || handle === 'right' || handle.includes('-')

const handleSupportsVerticalResize = (handle: CropHandle): boolean =>
  handle === 'top' || handle === 'bottom' || handle.includes('-')

const ImageEditorSurface = React.forwardRef<
  HTMLDivElement,
  ImageEditorSurfaceProps
>(
  (
    {
      source,
      value: edit,
      onValueChange,
      selectedAspectRatio,
      onSelectedAspectRatioChange,
      onValidityChange,
      showInlineNotices = true,
      adjustmentsHeader,
      cropControls,
      additionalControls,
      additionalControlsHidden = false,
      imageOverlay,
      stageNotice,
      stageActions,
      stageFooter,
      alternateInteraction,
      mode = 'edit',
      aspectRatios = [],
      showFreeAspectRatio = false,
      freeAspectRatioRange,
      disabled = false,
      onSourceLoadError,
      className,
    },
    forwardedRef,
  ) => {
    const cropInstructionsId = React.useId()
    const centerCropDisabledReasonId = React.useId()
    const appearanceControlId = React.useId()
    const previewFilterId = `${appearanceControlId}-preview-filter`
    const stageRef = React.useRef<HTMLFieldSetElement>(null)
    const [stageViewportRef, stageViewportDimensions] =
      useResizeObserver<HTMLDivElement>()
    const imageRef = React.useRef<HTMLImageElement>(null)
    const activeDragRef = React.useRef<ActiveCropDrag | null>(null)
    const setEdit = React.useCallback<
      React.Dispatch<React.SetStateAction<ImageEdit>>
    >(
      (nextValue) =>
        onValueChange(
          typeof nextValue === 'function' ? nextValue(edit) : nextValue,
        ),
      [edit, onValueChange],
    )
    const [sourceDimensions, setSourceDimensions] =
      React.useState<ImageDimensions | null>(() =>
        getProvidedDimensions(source.width, source.height),
      )
    const setSelectedAspectRatio = useCallbackRef(onSelectedAspectRatioChange)
    const [isCropDragging, setIsCropDragging] = React.useState(false)
    const [activeEditView, setActiveEditView] =
      React.useState<ImageEditorEditView>('crop-transform')
    const [identityFreeformIsSelected, setIdentityFreeformIsSelected] =
      React.useState(false)
    const [loadedSourceSrc, setLoadedSourceSrc] = React.useState<string | null>(
      null,
    )
    const [error, setError] = React.useState<string | null>(null)
    const handleValidityChange = useCallbackRef(onValidityChange)
    const imageLoaded = loadedSourceSrc === source.src

    useIsomorphicLayoutEffect(() => {
      const providedDimensions = getProvidedDimensions(
        source.width,
        source.height,
      )
      const imageElement = imageRef.current
      const decodedDimensions =
        imageElement?.getAttribute('src') === source.src &&
        imageElement.complete
          ? getProvidedDimensions(
              imageElement.naturalWidth,
              imageElement.naturalHeight,
            )
          : null
      const initialDimensions = decodedDimensions ?? providedDimensions

      setSourceDimensions(initialDimensions)
      setIsCropDragging(false)
      setActiveEditView('crop-transform')
      setIdentityFreeformIsSelected(false)
      setLoadedSourceSrc(decodedDimensions ? source.src : null)
      setError(null)
      activeDragRef.current = null
    }, [source.height, source.src, source.width])

    const orientedDimensions = sourceDimensions
      ? getOrientedImageDimensions(sourceDimensions, edit.rotation)
      : FALLBACK_DIMENSIONS
    const containedStageDimensions =
      stageViewportDimensions.width && stageViewportDimensions.height
        ? containImageDimensions(orientedDimensions, {
            width: stageViewportDimensions.width,
            height: stageViewportDimensions.height,
          })
        : null
    const croppedDimensions = sourceDimensions
      ? getCroppedImageDimensions(sourceDimensions, edit)
      : null
    const selectedRatio = aspectRatios.some(
      (aspectRatio) => aspectRatio.value === selectedAspectRatio,
    )
      ? selectedAspectRatio
      : null
    const cropGeometry = sourceDimensions
      ? getPixelCropGeometry(edit.crop, orientedDimensions)
      : null
    const isDirty = !isIdentityImageEdit(edit)
    const originalCrop = createIdentityImageEdit().crop
    const cropIsOriginal = normalizedCropsAreEqual(edit.crop, originalCrop)
    const previousCropWasOriginalRef = React.useRef(cropIsOriginal)
    const originalCropIsSelected =
      selectedRatio === null && cropIsOriginal && !identityFreeformIsSelected
    const freeformCropIsSelected =
      selectedRatio === null && (!cropIsOriginal || identityFreeformIsSelected)
    const centeredCrop = centerNormalizedCrop(edit.crop)
    const cropIsCentered =
      edit.crop.x === centeredCrop.x && edit.crop.y === centeredCrop.y
    const outputDimensionsAreSafe =
      croppedDimensions !== null &&
      croppedDimensions.width <= DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_DIMENSION &&
      croppedDimensions.height <= DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_DIMENSION
    const editorDraftIsValid =
      imageLoaded && error === null && (!isDirty || outputDimensionsAreSafe)
    const hasAlternateInteraction = alternateInteraction !== undefined
    const canEdit =
      mode === 'edit' &&
      sourceDimensions !== null &&
      error === null &&
      !disabled &&
      !hasAlternateInteraction
    const centerCropIsAriaDisabled = canEdit && cropIsCentered

    const editorIssue =
      error ??
      (imageLoaded && isDirty && !outputDimensionsAreSafe
        ? OUTPUT_LIMIT_ERROR
        : undefined)

    React.useEffect(() => {
      if (editorIssue) {
        handleValidityChange(false, editorIssue)
        return
      }

      handleValidityChange(editorDraftIsValid)
    }, [editorDraftIsValid, editorIssue, handleValidityChange])

    React.useEffect(() => {
      const cropWasOriginal = previousCropWasOriginalRef.current
      previousCropWasOriginalRef.current = cropIsOriginal

      if (cropIsOriginal && !cropWasOriginal) {
        setIdentityFreeformIsSelected(false)
      }
    }, [cropIsOriginal])

    useIsomorphicLayoutEffect(() => {
      if (mode === 'edit' && !disabled && !hasAlternateInteraction) return

      activeDragRef.current = null
      setIsCropDragging(false)
    }, [disabled, hasAlternateInteraction, mode])

    const updateCrop = React.useCallback(
      (
        action: CropAction,
        deltaX: number,
        deltaY: number,
        options: CropUpdateOptions = {},
      ): void => {
        if (!sourceDimensions) return

        setEdit((currentEdit) => {
          const stageBounds = stageRef.current?.getBoundingClientRect()
          const startingCrop = options.initialCrop ?? currentEdit.crop
          const currentOrientedDimensions = getOrientedImageDimensions(
            sourceDimensions,
            currentEdit.rotation,
          )
          const aspectRatio =
            selectedRatio ??
            (options.preserveInitialAspectRatio
              ? (startingCrop.width * currentOrientedDimensions.width) /
                (startingCrop.height * currentOrientedDimensions.height)
              : null)
          const minimumSize = getMinimumDisplayedCropSize(stageBounds)
          const crop =
            action.kind === 'move'
              ? moveNormalizedCrop(startingCrop, deltaX, deltaY)
              : resizeNormalizedCrop(
                  startingCrop,
                  action.handle,
                  deltaX,
                  deltaY,
                  {
                    ...(minimumSize === undefined ? {} : { minimumSize }),
                    ...(aspectRatio
                      ? {
                          aspectRatio: {
                            ratio: aspectRatio,
                            imageDimensions: currentOrientedDimensions,
                          },
                        }
                      : freeformCropIsSelected && freeAspectRatioRange
                        ? {
                            aspectRatioRange: {
                              ...freeAspectRatioRange,
                              imageDimensions: currentOrientedDimensions,
                            },
                          }
                        : {}),
                  },
                )

          return replaceCrop(currentEdit, crop)
        })
      },
      [
        freeAspectRatioRange,
        freeformCropIsSelected,
        selectedRatio,
        setEdit,
        sourceDimensions,
      ],
    )

    const handlePointerDown =
      (action: CropAction) =>
      (event: React.PointerEvent<HTMLButtonElement>): void => {
        if (!canEdit || event.button !== 0 || event.isPrimary === false) return

        if (typeof event.currentTarget.setPointerCapture === 'function') {
          event.currentTarget.setPointerCapture(event.pointerId)
        }
        activeDragRef.current = {
          action,
          pointerId: event.pointerId,
          originClientX: event.clientX,
          originClientY: event.clientY,
          initialCrop: edit.crop,
        }
        setIsCropDragging(true)
      }

    const handlePointerMove = (
      event: React.PointerEvent<HTMLButtonElement>,
    ): void => {
      const activeDrag = activeDragRef.current
      const stageBounds = stageRef.current?.getBoundingClientRect()

      if (
        !canEdit ||
        !activeDrag ||
        activeDrag.pointerId !== event.pointerId ||
        !stageBounds ||
        stageBounds.width <= 0 ||
        stageBounds.height <= 0
      ) {
        return
      }

      event.preventDefault()
      let clientDeltaX = event.clientX - activeDrag.originClientX
      let clientDeltaY = event.clientY - activeDrag.originClientY

      if (event.shiftKey && activeDrag.action.kind === 'move') {
        if (Math.abs(clientDeltaX) >= Math.abs(clientDeltaY)) {
          clientDeltaY = 0
        } else {
          clientDeltaX = 0
        }
      }

      updateCrop(
        activeDrag.action,
        clientDeltaX / stageBounds.width,
        clientDeltaY / stageBounds.height,
        {
          initialCrop: activeDrag.initialCrop,
          preserveInitialAspectRatio:
            event.shiftKey && activeDrag.action.kind === 'resize',
        },
      )
    }

    const endPointerDrag = (
      event: React.PointerEvent<HTMLButtonElement>,
    ): void => {
      if (activeDragRef.current?.pointerId !== event.pointerId) return
      activeDragRef.current = null
      setIsCropDragging(false)
    }

    const handleEscapeKeyDown = React.useCallback(
      (event: KeyboardEvent): void => {
        const activeDrag = activeDragRef.current
        if (!activeDrag) return

        event.preventDefault()
        activeDragRef.current = null
        setEdit((currentEdit) =>
          replaceCrop(currentEdit, activeDrag.initialCrop),
        )
        setIsCropDragging(false)
      },
      [setEdit],
    )

    React.useEffect(() => {
      if (!isCropDragging) return

      const handleDocumentKeyDown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') handleEscapeKeyDown(event)
      }
      window.addEventListener('keydown', handleDocumentKeyDown, true)

      return (): void =>
        window.removeEventListener('keydown', handleDocumentKeyDown, true)
    }, [handleEscapeKeyDown, isCropDragging])

    const handleCropKeyDown =
      (action: CropAction) =>
      (event: React.KeyboardEvent<HTMLButtonElement>): void => {
        if (event.key === 'Escape') {
          handleEscapeKeyDown(event.nativeEvent)
          return
        }
        if (!canEdit) return

        const step = event.shiftKey ? 10 : 1
        let deltaX = 0
        let deltaY = 0

        if (event.key === 'ArrowLeft') deltaX = -step / orientedDimensions.width
        if (event.key === 'ArrowRight') deltaX = step / orientedDimensions.width
        if (event.key === 'ArrowUp') deltaY = -step / orientedDimensions.height
        if (event.key === 'ArrowDown') deltaY = step / orientedDimensions.height

        if (deltaX === 0 && deltaY === 0) return
        if (
          action.kind === 'resize' &&
          ((deltaX !== 0 && !handleSupportsHorizontalResize(action.handle)) ||
            (deltaY !== 0 && !handleSupportsVerticalResize(action.handle)))
        ) {
          return
        }

        event.preventDefault()
        updateCrop(action, deltaX, deltaY)
      }

    const handleImageLoad = (
      event: React.SyntheticEvent<HTMLImageElement>,
    ): void => {
      const loadedDimensions =
        getProvidedDimensions(
          event.currentTarget.naturalWidth,
          event.currentTarget.naturalHeight,
        ) ?? getProvidedDimensions(source.width, source.height)

      if (!loadedDimensions) {
        setError('This image does not report usable dimensions.')
        setLoadedSourceSrc(null)
        return
      }

      setSourceDimensions(loadedDimensions)
      setLoadedSourceSrc(source.src)
      setError(null)
    }

    const handleAspectRatioChange = (ratio: number): void => {
      if (!sourceDimensions) return

      setIdentityFreeformIsSelected(false)
      setSelectedAspectRatio(ratio)

      setEdit((currentEdit) =>
        replaceCrop(
          currentEdit,
          fitCropToAspectRatio(
            createIdentityImageEdit().crop,
            ratio,
            getOrientedImageDimensions(sourceDimensions, currentEdit.rotation),
          ),
        ),
      )
    }

    const handleOriginalCrop = (): void => {
      if (!sourceDimensions) return

      setIdentityFreeformIsSelected(false)
      setSelectedAspectRatio(null)
      setEdit((currentEdit) =>
        replaceCrop(currentEdit, createIdentityImageEdit().crop),
      )
    }

    const handleFreeAspectRatioActivation = (): void => {
      if (!sourceDimensions) return

      setIdentityFreeformIsSelected(true)
      setSelectedAspectRatio(null)

      setEdit((currentEdit) => {
        if (!freeAspectRatioRange) return currentEdit

        const currentOrientedDimensions = getOrientedImageDimensions(
          sourceDimensions,
          currentEdit.rotation,
        )
        const currentAspectRatio =
          (currentEdit.crop.width * currentOrientedDimensions.width) /
          (currentEdit.crop.height * currentOrientedDimensions.height)
        const constrainedAspectRatio = Math.min(
          freeAspectRatioRange.max,
          Math.max(freeAspectRatioRange.min, currentAspectRatio),
        )

        return replaceCrop(
          currentEdit,
          fitCropToAspectRatio(
            currentEdit.crop,
            constrainedAspectRatio,
            currentOrientedDimensions,
          ),
        )
      })
    }

    const handleRotateClockwise = (): void => {
      if (!sourceDimensions) return

      setEdit((currentEdit) =>
        rotateImageEditClockwise(
          snapEditCropToPixels(
            currentEdit,
            getOrientedImageDimensions(sourceDimensions, currentEdit.rotation),
          ),
        ),
      )
      if (selectedRatio !== 1) setSelectedAspectRatio(null)
    }

    const handleRotateCounterClockwise = (): void => {
      if (!sourceDimensions) return

      setEdit((currentEdit) =>
        rotateImageEditCounterclockwise(
          snapEditCropToPixels(
            currentEdit,
            getOrientedImageDimensions(sourceDimensions, currentEdit.rotation),
          ),
        ),
      )
      if (selectedRatio !== 1) setSelectedAspectRatio(null)
    }

    const handleCenterCrop = (): void => {
      if (!canEdit || cropIsCentered) return

      setEdit((currentEdit) =>
        replaceCrop(currentEdit, centerNormalizedCrop(currentEdit.crop)),
      )
    }

    const handleStraighteningChange = (straightening: number): void => {
      if (!canEdit) return

      setEdit((currentEdit) => ({ ...currentEdit, straightening }))
    }

    const handleEditViewChange = (value: string): void => {
      if (!isImageEditorEditView(value)) return

      setActiveEditView(value)
    }

    const handleAdjustmentChange =
      (key: AppearanceAdjustmentKey) =>
      (value: number): void => {
        if (!canEdit) return

        setEdit((currentEdit) => ({ ...currentEdit, [key]: value }))
      }

    const handleFilterIntensityChange = (filterIntensity: number): void => {
      if (!canEdit) return

      setEdit((currentEdit) => ({ ...currentEdit, filterIntensity }))
    }

    const handleFilterPresetChange = (value: string): void => {
      if (!canEdit) return

      const preset = FILTER_PRESETS.find((option) => option.value === value)
      if (!preset) return
      setEdit((currentEdit) => ({
        ...currentEdit,
        filterPreset: preset.value,
      }))
    }
    const handleFlipHorizontal = (): void => {
      if (!sourceDimensions) return

      setEdit((currentEdit) =>
        flipImageEditHorizontally(
          snapEditCropToPixels(
            currentEdit,
            getOrientedImageDimensions(sourceDimensions, currentEdit.rotation),
          ),
        ),
      )
    }

    const handleFlipVertical = (): void => {
      if (!sourceDimensions) return

      setEdit((currentEdit) =>
        flipImageEditVertically(
          snapEditCropToPixels(
            currentEdit,
            getOrientedImageDimensions(sourceDimensions, currentEdit.rotation),
          ),
        ),
      )
    }

    const cropStyle: React.CSSProperties = {
      left: `${edit.crop.x * 100}%`,
      top: `${edit.crop.y * 100}%`,
      width: `${edit.crop.width * 100}%`,
      height: `${edit.crop.height * 100}%`,
    }
    const sourceRatio = sourceDimensions
      ? sourceDimensions.width / sourceDimensions.height
      : 1
    const isQuarterTurn = edit.rotation === 90 || edit.rotation === 270
    const imageStyle: React.CSSProperties = {
      width: isQuarterTurn ? `${sourceRatio * 100}%` : '100%',
      height: isQuarterTurn ? `${(1 / sourceRatio) * 100}%` : '100%',
      transform: getImageEditCssTransform(
        sourceDimensions ?? FALLBACK_DIMENSIONS,
        edit,
      ),
      filter: getImageAppearanceCssFilter(edit, previewFilterId),
    }
    const stageStyle: React.CSSProperties = containedStageDimensions
      ? {
          width: `${containedStageDimensions.width}px`,
          height: `${containedStageDimensions.height}px`,
        }
      : {
          aspectRatio: `${orientedDimensions.width} / ${orientedDimensions.height}`,
          ...(orientedDimensions.width >= orientedDimensions.height
            ? { width: '100%' }
            : { height: '100%' }),
        }
    const cropDimensions = croppedDimensions
      ? `${croppedDimensions.width} by ${croppedDimensions.height} pixels`
      : 'Image dimensions unavailable'
    const transformDescription = [
      `${edit.rotation} degree rotation`,
      edit.straightening === 0
        ? null
        : `${edit.straightening} degree straightening`,
      edit.flipHorizontal ? 'horizontally flipped' : null,
      edit.flipVertical ? 'vertically flipped' : null,
    ]
      .filter((description) => description !== null)
      .join(', ')
    const cropPosition = cropGeometry
      ? `${cropGeometry.left} pixels from the left and ${cropGeometry.top} pixels from the top`
      : 'unavailable'
    const cropStatus = `Crop ${cropDimensions}, positioned ${cropPosition}. ${transformDescription}.`
    const [settledCropStatus, setSettledCropStatus] = React.useState(cropStatus)

    React.useEffect(() => {
      if (isCropDragging) return

      const timeout = window.setTimeout(() => {
        setSettledCropStatus(cropStatus)
      }, 300)

      return (): void => window.clearTimeout(timeout)
    }, [cropStatus, isCropDragging])
    const outputLimitErrorVisible =
      imageLoaded && isDirty && !outputDimensionsAreSafe
    const centerCropButton = (
      <Button
        variant="secondary"
        size="medium"
        className={centerCropIsAriaDisabled ? styles.disabledAction : undefined}
        disabled={!canEdit}
        aria-label="Center crop"
        aria-disabled={centerCropIsAriaDisabled ? true : undefined}
        aria-describedby={
          centerCropIsAriaDisabled ? centerCropDisabledReasonId : undefined
        }
        onClick={handleCenterCrop}
      >
        <CrosshairIcon />
        Center
      </Button>
    )

    const editorContent = (
      <>
        <div className={styles.editorLayout} data-mode={mode}>
          <section className={styles.stagePanel} aria-label="Image preview">
            <div
              ref={stageViewportRef}
              className={styles.stageViewport}
              data-has-stage-actions={stageActions ? 'true' : undefined}
            >
              {stageNotice ? (
                <div className={styles.stageNotice}>{stageNotice}</div>
              ) : null}
              {alternateInteraction?.stageOverlay}
              <fieldset
                ref={stageRef}
                className={styles.imageStage}
                style={stageStyle}
                aria-describedby={
                  mode === 'edit' && !hasAlternateInteraction
                    ? cropInstructionsId
                    : undefined
                }
                aria-busy={!imageLoaded && error === null}
              >
                <VisuallyHidden as="legend">
                  {hasAlternateInteraction
                    ? alternateInteraction.canvasLabel
                    : 'Image crop area'}
                </VisuallyHidden>
                <div className={styles.imageClip}>
                  {/* oxlint-disable-next-line next/no-img-element */}
                  <img
                    ref={imageRef}
                    className={styles.image}
                    style={imageStyle}
                    src={source.src}
                    alt={source.alt ?? 'Image being edited'}
                    crossOrigin={
                      source.crossOrigin === null
                        ? undefined
                        : (source.crossOrigin ?? 'anonymous')
                    }
                    draggable={false}
                    onLoad={handleImageLoad}
                    onError={() => {
                      setLoadedSourceSrc(null)
                      setError('This image could not be loaded.')
                      onSourceLoadError?.()
                    }}
                  />
                  <ImageAppearanceFilter
                    id={previewFilterId}
                    appearance={edit}
                  />
                </div>

                {imageOverlay ? (
                  <div className={styles.imageOverlay}>{imageOverlay}</div>
                ) : null}

                {alternateInteraction ? (
                  <div className={styles.cropOverlay} style={cropStyle}>
                    {alternateInteraction.cropOverlay}
                  </div>
                ) : mode === 'preview' ? (
                  <div className={styles.previewCropFrame} style={cropStyle} />
                ) : (
                  <div
                    className={styles.cropFrame}
                    style={cropStyle}
                    data-dragging={isCropDragging ? 'true' : undefined}
                  >
                    <UnstyledButton
                      type="button"
                      className={styles.moveHandle}
                      aria-label="Move crop selection"
                      disabled={!canEdit}
                      onPointerDown={handlePointerDown({ kind: 'move' })}
                      onPointerMove={handlePointerMove}
                      onPointerUp={endPointerDrag}
                      onPointerCancel={endPointerDrag}
                      onLostPointerCapture={endPointerDrag}
                      onKeyDown={handleCropKeyDown({ kind: 'move' })}
                    />
                    {selectedRatio !== null ||
                    (showFreeAspectRatio && freeformCropIsSelected)
                      ? cropHandles.map(
                          ({ handle, label, className: handleClassName }) => (
                            <UnstyledButton
                              key={handle}
                              type="button"
                              className={clsx(
                                styles.resizeHandle,
                                handleClassName,
                              )}
                              aria-label={`Resize crop from ${label}`}
                              tabIndex={cornerCropHandles.has(handle) ? 0 : -1}
                              disabled={!canEdit}
                              onPointerDown={handlePointerDown({
                                kind: 'resize',
                                handle,
                              })}
                              onPointerMove={handlePointerMove}
                              onPointerUp={endPointerDrag}
                              onPointerCancel={endPointerDrag}
                              onLostPointerCapture={endPointerDrag}
                              onKeyDown={handleCropKeyDown({
                                kind: 'resize',
                                handle,
                              })}
                            />
                          ),
                        )
                      : null}
                    <div className={styles.cropGuides} aria-hidden="true">
                      <span
                        className={clsx(
                          styles.cropGuide,
                          styles.cropGuideVertical,
                          styles.cropGuideFirst,
                        )}
                      />
                      <span
                        className={clsx(
                          styles.cropGuide,
                          styles.cropGuideVertical,
                          styles.cropGuideSecond,
                        )}
                      />
                      <span
                        className={clsx(
                          styles.cropGuide,
                          styles.cropGuideHorizontal,
                          styles.cropGuideFirst,
                        )}
                      />
                      <span
                        className={clsx(
                          styles.cropGuide,
                          styles.cropGuideHorizontal,
                          styles.cropGuideSecond,
                        )}
                      />
                    </div>
                  </div>
                )}
              </fieldset>
              {stageActions ? (
                <div className={styles.stageActions}>{stageActions}</div>
              ) : null}
            </div>
            {mode === 'edit' && !hasAlternateInteraction && (
              <VisuallyHidden id={cropInstructionsId}>
                Use arrow keys to move the crop. Hold Shift for larger steps.
                {showFreeAspectRatio
                  ? ' Select Freeform or a crop ratio to resize.'
                  : ' Select a crop ratio to resize.'}
              </VisuallyHidden>
            )}
          </section>

          {stageFooter ? (
            <div className={styles.stageFooter}>{stageFooter}</div>
          ) : null}

          <section
            className={styles.adjustmentsPanel}
            data-mode={mode}
            aria-label="Image adjustments"
          >
            {adjustmentsHeader ? (
              <div className={styles.adjustmentsHeader}>
                {adjustmentsHeader}
              </div>
            ) : null}
            {mode === 'edit' ? (
              <Tabs
                size="small"
                value={activeEditView}
                onChange={handleEditViewChange}
              >
                <Tabs.TabList className={styles.editViewTabList}>
                  <Tabs.Tab
                    value="crop-transform"
                    className={styles.editViewTab}
                  >
                    Crop
                  </Tabs.Tab>
                  <Tabs.Tab value="appearance" className={styles.editViewTab}>
                    Appearance
                  </Tabs.Tab>
                </Tabs.TabList>

                {activeEditView === 'crop-transform' ? (
                  <Tabs.Panel
                    value="crop-transform"
                    className={styles.editViewPanel}
                    asChild
                  >
                    <div className={styles.controlGroup}>
                      <div className={styles.cropHeader}>
                        <Text
                          as="h4"
                          size="md"
                          weight="semibold"
                          color="default"
                          className={styles.groupTitle}
                        >
                          Crop &amp; Transform
                        </Text>
                      </div>
                      <fieldset className={styles.ratioFieldset}>
                        <VisuallyHidden as="legend">Crop ratio</VisuallyHidden>
                        <div className={styles.ratioGroup}>
                          {showFreeAspectRatio ? (
                            <UnstyledButton
                              type="button"
                              className={styles.ratioCard}
                              disabled={!canEdit}
                              aria-label="Freeform"
                              aria-describedby={`${cropInstructionsId}-ratio-freeform-description`}
                              aria-pressed={freeformCropIsSelected}
                              onClick={handleFreeAspectRatioActivation}
                            >
                              <span
                                className={clsx(
                                  styles.ratioShape,
                                  styles.ratioShapeFree,
                                )}
                                aria-hidden="true"
                              />
                              <span className={styles.ratioCardLabel}>
                                Freeform
                                <span
                                  id={`${cropInstructionsId}-ratio-freeform-description`}
                                  className={styles.ratioCardDescription}
                                >
                                  Any size
                                </span>
                              </span>
                            </UnstyledButton>
                          ) : null}
                          <UnstyledButton
                            type="button"
                            className={styles.ratioCard}
                            disabled={!canEdit}
                            aria-label="Original"
                            aria-describedby={`${cropInstructionsId}-ratio-original-description`}
                            aria-pressed={originalCropIsSelected}
                            onClick={handleOriginalCrop}
                          >
                            <span
                              className={clsx(
                                styles.ratioShape,
                                styles.ratioShapeOriginal,
                              )}
                              aria-hidden="true"
                            />
                            <span className={styles.ratioCardLabel}>
                              Original
                              <span
                                id={`${cropInstructionsId}-ratio-original-description`}
                                className={styles.ratioCardDescription}
                              >
                                Full image
                              </span>
                            </span>
                          </UnstyledButton>
                          {aspectRatios.map((aspectRatio, index) => (
                            <UnstyledButton
                              key={`${aspectRatio.label}-${aspectRatio.value}`}
                              type="button"
                              className={styles.ratioCard}
                              disabled={!canEdit}
                              aria-label={aspectRatio.label}
                              aria-describedby={`${cropInstructionsId}-ratio-${index}-description`}
                              aria-pressed={selectedRatio === aspectRatio.value}
                              onClick={() =>
                                handleAspectRatioChange(aspectRatio.value)
                              }
                            >
                              <span
                                className={clsx(
                                  styles.ratioShape,
                                  aspectRatio.value === 1 &&
                                    styles.ratioShapeSquare,
                                  aspectRatio.value < 1 &&
                                    styles.ratioShapePortrait,
                                  aspectRatio.value > 1 &&
                                    styles.ratioShapeLandscape,
                                )}
                                aria-hidden="true"
                              />
                              <span className={styles.ratioCardLabel}>
                                {aspectRatio.label}
                                <span
                                  id={`${cropInstructionsId}-ratio-${index}-description`}
                                  className={styles.ratioCardDescription}
                                >
                                  {aspectRatio.value === 1
                                    ? 'Square'
                                    : aspectRatio.value < 1
                                      ? 'Portrait'
                                      : 'Landscape'}
                                </span>
                              </span>
                            </UnstyledButton>
                          ))}
                        </div>
                      </fieldset>

                      <div className={styles.transformGroup}>
                        <Text
                          as="h4"
                          size="md"
                          weight="semibold"
                          color="default"
                          className={styles.groupTitle}
                        >
                          Rotate &amp; Flip
                        </Text>
                        <div className={styles.transformControls}>
                          <IconButton
                            variant="secondary"
                            size="medium"
                            disabled={!canEdit}
                            label="Rotate counterclockwise 90 degrees"
                            tooltip="Rotate counterclockwise 90 degrees"
                            onClick={handleRotateCounterClockwise}
                          >
                            <RotateCwIcon
                              className={styles.counterClockwiseIcon}
                            />
                          </IconButton>
                          <IconButton
                            variant="secondary"
                            size="medium"
                            disabled={!canEdit}
                            label="Rotate clockwise 90 degrees"
                            tooltip="Rotate clockwise 90 degrees"
                            onClick={handleRotateClockwise}
                          >
                            <RotateCwIcon />
                          </IconButton>
                          <IconButton
                            variant="secondary"
                            size="medium"
                            disabled={!canEdit}
                            label="Flip horizontal"
                            tooltip="Flip horizontal"
                            aria-pressed={edit.flipHorizontal}
                            onClick={handleFlipHorizontal}
                          >
                            <FlipHorizontal2Icon />
                          </IconButton>
                          <IconButton
                            variant="secondary"
                            size="medium"
                            disabled={!canEdit}
                            label="Flip vertical"
                            tooltip="Flip vertical"
                            aria-pressed={edit.flipVertical}
                            onClick={handleFlipVertical}
                          >
                            <FlipVertical2Icon />
                          </IconButton>
                        </div>
                        <Slider
                          label="Straighten"
                          valueLabel={formatStraightening(edit.straightening)}
                          min={IMAGE_EDITOR_MIN_STRAIGHTENING}
                          max={IMAGE_EDITOR_MAX_STRAIGHTENING}
                          step={1}
                          value={edit.straightening}
                          origin={0}
                          disabled={!canEdit}
                          aria-valuetext={getStraighteningValueText(
                            edit.straightening,
                          )}
                          onValueChange={handleStraighteningChange}
                        />
                      </div>

                      <fieldset className={styles.cropTools}>
                        <VisuallyHidden as="legend">Crop tools</VisuallyHidden>
                        <div className={styles.cropUtilityGrid}>
                          <Tooltip
                            content={
                              centerCropIsAriaDisabled
                                ? CENTER_CROP_DISABLED_REASON
                                : CENTER_CROP_TOOLTIP
                            }
                          >
                            {centerCropButton}
                          </Tooltip>
                          {cropControls ? (
                            <div className={styles.cropControls}>
                              {cropControls}
                            </div>
                          ) : null}
                        </div>
                        {centerCropIsAriaDisabled ? (
                          <VisuallyHidden id={centerCropDisabledReasonId}>
                            {CENTER_CROP_DISABLED_REASON}
                          </VisuallyHidden>
                        ) : null}
                      </fieldset>
                    </div>
                  </Tabs.Panel>
                ) : null}

                {activeEditView === 'appearance' ? (
                  <Tabs.Panel
                    value="appearance"
                    className={styles.editViewPanel}
                    asChild
                  >
                    <div className={styles.controlGroup}>
                      <fieldset className={styles.appearanceFieldset}>
                        <legend className={styles.groupLegend}>
                          <Text
                            as="span"
                            size="md"
                            weight="semibold"
                            color="default"
                          >
                            Filters
                          </Text>
                        </legend>
                        <ToggleGroup
                          className={styles.filterGroup}
                          value={edit.filterPreset}
                          disabled={!canEdit}
                          onChange={handleFilterPresetChange}
                        >
                          {FILTER_PRESETS.map((preset) => (
                            <ToggleGroup.Item
                              key={preset.value}
                              value={preset.value}
                              className={styles.filterCard}
                              aria-label={`${preset.label} filter`}
                            >
                              <span
                                className={styles.filterThumbnail}
                                aria-hidden="true"
                              >
                                {/* Thumbnails preview the decoded editing source. */}
                                {/* oxlint-disable-next-line next/no-img-element */}
                                <img
                                  className={styles.filterThumbnailImage}
                                  style={{
                                    filter: getImageAppearanceCssFilter(
                                      getFilterThumbnailAppearance(
                                        preset.value,
                                      ),
                                      `${appearanceControlId}-filter-${preset.value}`,
                                    ),
                                  }}
                                  src={source.src}
                                  alt=""
                                  crossOrigin="anonymous"
                                  draggable={false}
                                />
                              </span>
                              <span className={styles.filterCardLabel}>
                                {preset.label}
                              </span>
                            </ToggleGroup.Item>
                          ))}
                        </ToggleGroup>
                        {FILTER_PRESETS.filter(
                          (preset) => preset.value !== 'none',
                        ).map((preset) => (
                          <ImageAppearanceFilter
                            key={preset.value}
                            id={`${appearanceControlId}-filter-${preset.value}`}
                            appearance={getFilterThumbnailAppearance(
                              preset.value,
                            )}
                          />
                        ))}
                        {edit.filterPreset !== 'none' ? (
                          <Slider
                            label="Filter intensity"
                            valueLabel={`${edit.filterIntensity}%`}
                            min={IMAGE_EDITOR_MIN_FILTER_INTENSITY}
                            max={IMAGE_EDITOR_MAX_FILTER_INTENSITY}
                            step={1}
                            value={edit.filterIntensity}
                            disabled={!canEdit}
                            aria-valuetext={`${edit.filterIntensity}% intensity`}
                            onValueChange={handleFilterIntensityChange}
                          />
                        ) : null}
                      </fieldset>

                      <div className={styles.adjustGroup}>
                        <Text
                          as="h4"
                          size="md"
                          weight="semibold"
                          color="default"
                          className={styles.groupTitle}
                        >
                          Adjust
                        </Text>
                        {APPEARANCE_ADJUSTMENTS.map(
                          ({ key, label, getValueText }) => (
                            <Slider
                              key={key}
                              label={label}
                              valueLabel={formatAdjustment(edit[key])}
                              min={IMAGE_EDITOR_MIN_ADJUSTMENT}
                              max={IMAGE_EDITOR_MAX_ADJUSTMENT}
                              step={1}
                              value={edit[key]}
                              origin={0}
                              disabled={!canEdit}
                              aria-valuetext={getValueText(edit[key])}
                              onValueChange={handleAdjustmentChange(key)}
                            />
                          ),
                        )}
                      </div>
                    </div>
                  </Tabs.Panel>
                ) : null}
              </Tabs>
            ) : null}

            {additionalControls ? (
              <div
                className={styles.additionalControls}
                hidden={additionalControlsHidden}
              >
                {additionalControls}
              </div>
            ) : null}

            {showInlineNotices && outputLimitErrorVisible && (
              <Notice variant="error" className={styles.notice}>
                {OUTPUT_LIMIT_ERROR}
              </Notice>
            )}
            {showInlineNotices && error && (
              <Notice variant="error" className={styles.notice}>
                {error}
              </Notice>
            )}
          </section>
        </div>

        <VisuallyHidden as="output" aria-live="polite" aria-atomic="true">
          {isCropDragging ? 'Adjusting crop.' : settledCropStatus}
        </VisuallyHidden>
      </>
    )

    return (
      <div ref={forwardedRef} className={clsx(styles.surface, className)}>
        {editorContent}
      </div>
    )
  },
)

ImageEditorSurface.displayName = 'ImageEditorSurface'

export { ImageEditorSurface }
export type {
  ImageEditorAspectRatio,
  ImageEditorAspectRatioRange,
  ImageEditorSource,
  ImageEditorSurfaceAlternateInteraction,
  ImageEditorSurfaceProps,
}
